import {
  Box,
  Divider,
  Flex,
  Portal,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from '@chakra-ui/react'
import { Column, Table } from '@tanstack/react-table'
import React, { useEffect, useRef } from 'react'

import { Icon } from '../Icons/Icon'

interface ColumnMenuProps<TData extends Record<string, any>> {
  isOpen: boolean
  x: number
  y: number
  onClose: () => void
  onAutosizeColumn: (columnId: string) => void
  onAutosizeAllColumns: () => void
  onResetColumns: () => void
  column: Column<TData, unknown>
  table: Table<TData>
}

export const ColumnMenu = <TData extends Record<string, any>>({
  isOpen,
  x,
  y,
  onClose,
  onAutosizeColumn,
  onAutosizeAllColumns,
  onResetColumns,
  column,
  table,
}: ColumnMenuProps<TData>) => {
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      if (!isOpen || menuRef.current?.contains(event.target as Node)) {
        return
      }

      onClose()
    }

    document.addEventListener('pointerdown', handlePointerDown)
    return () => document.removeEventListener('pointerdown', handlePointerDown)
  }, [isOpen, onClose])

  if (!isOpen) return null

  return (
    <Portal>
      <Box
        position="fixed"
        top={y}
        left={x}
        zIndex={1400}
        onClick={e => e.stopPropagation()}
      >
        <Box
          ref={menuRef}
          bg="white"
          boxShadow="0px 4px 12px rgba(0, 0, 0, 0.15)"
          borderRadius="8px"
          minWidth="250px"
          overflow="hidden"
        >
          <Tabs isFitted variant="line">
            <TabList borderBottom="1px solid var(--chakra-colors-presets-neutral-20)">
              <Tab
                _selected={{
                  color: 'presets.blue.60',
                  borderBottom:
                    '2px solid var(--chakra-colors-presets-blue-60)',
                }}
                color="presets.neutral.90"
              >
                <Icon icon="EllipsisMenuIcon" boxSize="18px" />
              </Tab>
              <Tab
                _selected={{
                  color: 'presets.blue.60',
                  borderBottom:
                    '2px solid var(--chakra-colors-presets-blue-60)',
                }}
                color="presets.neutral.90"
              >
                <Icon icon="ItemsIcon" boxSize="18px" />
              </Tab>
            </TabList>

            <TabPanels>
              <TabPanel p={0}>
                <Flex direction="column" py={2}>
                  {/* Pin Submenu (Simplified for now) */}
                  <Flex direction="column">
                    <Flex
                      as="button"
                      align="center"
                      w="full"
                      px={4}
                      py={2}
                      _hover={{ bg: 'presets.neutral.10' }}
                      onClick={e => {
                        e.stopPropagation()
                        const currentPin = column.getIsPinned()
                        column.pin(currentPin === 'left' ? false : 'left')
                        onClose()
                      }}
                    >
                      <Icon icon="PinIcon" boxSize="18px" mr={3} />
                      <Text fontSize="14px" fontWeight={500}>
                        {column.getIsPinned() === 'left'
                          ? 'Unpin Column'
                          : 'Pin Left'}
                      </Text>
                    </Flex>
                    <Flex
                      as="button"
                      align="center"
                      w="full"
                      px={4}
                      py={2}
                      _hover={{ bg: 'presets.neutral.10' }}
                      onClick={e => {
                        e.stopPropagation()
                        const currentPin = column.getIsPinned()
                        column.pin(currentPin === 'right' ? false : 'right')
                        onClose()
                      }}
                    >
                      <Box w="18px" h="18px" mr={3} />
                      <Text fontSize="14px" fontWeight={500}>
                        {column.getIsPinned() === 'right'
                          ? 'Unpin Column'
                          : 'Pin Right'}
                      </Text>
                    </Flex>
                  </Flex>

                  <Divider borderColor="presets.neutral.20" />

                  <Flex
                    as="button"
                    align="center"
                    w="full"
                    px={4}
                    py={2}
                    _hover={{ bg: 'presets.neutral.10' }}
                    onClick={() => {
                      onAutosizeColumn(column.id)
                      onClose()
                    }}
                  >
                    <Text fontSize="14px" fontWeight={500}>
                      Autosize This Column
                    </Text>
                  </Flex>
                  <Flex
                    as="button"
                    align="center"
                    w="full"
                    px={4}
                    py={2}
                    _hover={{ bg: 'presets.neutral.10' }}
                    onClick={() => {
                      onAutosizeAllColumns()
                      onClose()
                    }}
                  >
                    <Text fontSize="14px" fontWeight={500}>
                      Autosize All Columns
                    </Text>
                  </Flex>

                  <Divider borderColor="presets.neutral.20" />

                  <Flex
                    as="button"
                    align="center"
                    w="full"
                    px={4}
                    py={2}
                    _hover={{ bg: 'presets.neutral.10' }}
                    onClick={() => {
                      onResetColumns()
                      onClose()
                    }}
                  >
                    <Text fontSize="14px" fontWeight={500}>
                      Reset Columns
                    </Text>
                  </Flex>
                </Flex>
              </TabPanel>
              <TabPanel p={0}>
                <Flex direction="column" py={2} maxH="300px" overflowY="auto">
                  {table.getAllLeafColumns().map(col => {
                    return (
                      <Flex
                        key={col.id}
                        as="label"
                        align="center"
                        w="full"
                        px={4}
                        py={2}
                        cursor="pointer"
                        _hover={{ bg: 'presets.neutral.10' }}
                      >
                        <Flex align="center" gap={2}>
                          <input
                            type="checkbox"
                            checked={col.getIsVisible()}
                            onChange={() => col.toggleVisibility()}
                            style={{ cursor: 'pointer' }}
                          />
                          <Text fontSize="14px" fontWeight={500}>
                            {typeof col.columnDef.header === 'string'
                              ? col.columnDef.header
                              : col.id}
                          </Text>
                        </Flex>
                      </Flex>
                    )
                  })}
                </Flex>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Box>
      </Box>
    </Portal>
  )
}
